import 'dayjs/locale/ko';
import 'dayjs/locale/en';
import 'dayjs/locale/zh-cn';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';

import { usePostAnnualFeePaidApi } from '@/api/payment';
import { OutlineButton, PrimaryButton } from '@/components/common/Button/style';
import ModalBox from '@/components/common/Modal/ModalBox/ModalBox';
import ModalPortal from '@/components/common/Modal/ModalPortal/ModalPortal';
import PaymentModal from '@/components/common/Modal/PaymentModal/PaymentModal';
import SingleArrowNext from '@/components/Icon/SingleArrowNext';
import { CONF_TITLE } from '@/constants/menu';
import { PAYMENT_GOODS_CONFERENCE_FEE } from '@/constants/payment';
import { normalizeSocietyMenu, SOCIETY_STEP_REGISTER_CONFIRM } from '@/constants/society';
import { useModalStore } from '@/store/modalStore';
import { useTokenStore } from '@/store/tokenStore';

import * as S from './style';

const DAYJS_LOCALE_BY_LANGUAGE: Record<string, string> = {
	ko: 'ko',
	en: 'en',
	zh: 'zh-cn',
};

const SocietyRegister = () => {
	const { t, i18n } = useTranslation();
	const currentLanguage = (i18n.resolvedLanguage ?? i18n.language ?? 'ko').slice(0, 2);
	const currentDayjsLocale = DAYJS_LOCALE_BY_LANGUAGE[currentLanguage] ?? 'en';

	dayjs.locale(currentDayjsLocale);
	const START_DATE = '2026-04-27T00:00:00+09:00';
	const END_DATE = '2026-06-12T00:00:00+09:00';
	const REFUND_DATE = '2026-06-24T00:00:00+09:00';

	const { showModal, closeModal } = useModalStore();
	const { isLogin } = useTokenStore();
	const [isOpen, setIsOpen] = useState(false);
	const [isRegistering, setIsRegistering] = useState(false);
	const [memberType, setMemberType] = useState('');
	const navigate = useNavigate();
	const location = useLocation();
	const params = normalizeSocietyMenu(location.state?.menu);
	const { mutateAsync: annualFeePaid } = usePostAnnualFeePaidApi();

	const formatLongDate = (value: string) => dayjs(value).locale(currentDayjsLocale).format(t('society.register.dateFormat.long'));
	const formatShortDate = (value: string) => dayjs(value).locale(currentDayjsLocale).format(t('society.register.dateFormat.short'));

	const validateRegistrationPeriod = () => {
		const now = new Date();
		const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
		const startDate = new Date(START_DATE);
		const deadline = new Date(END_DATE);

		if (koreaTime < startDate) {
			showModal(t('society.register.periodNotStarted'), 'confirm');
			return false;
		}

		// if (koreaTime >= deadline) {
		// 	showModal(t('society.register.periodClosed'), 'confirm');
		// 	return false;
		// }

		return true;
	};

	const handleRegister = async () => {
		if (isRegistering) {
			return;
		}

		if (!validateRegistrationPeriod()) {
			return;
		}

		if (!isLogin()) {
			showModal(t('common.alerts.loginRequired'), 'login', t('common.actions.goToLogin'), () => navigate('/auth/login'));
			return;
		}

		setIsRegistering(true);
		try {
			const res = await annualFeePaid({
				goods_name: CONF_TITLE,
			});
			if (res.data.success) {
				setMemberType(res.data?.data?.member_type);
				setIsOpen(true);
			} else if (res.data?.error.code === '80') {
				showModal(t('society.register.alreadyPaid'), 'login', t('common.actions.confirm'), closeModal);
			} else {
				showModal(t('society.register.error'), 'register');
			}
		} finally {
			setIsRegistering(false);
		}
	};

	const handleConfirm = () => {
		if (isLogin()) {
			navigate('/society', { state: { step: SOCIETY_STEP_REGISTER_CONFIRM, menu: params } });
		} else {
			showModal(t('common.alerts.loginRequired'), 'login', t('common.actions.goToLogin'), () => navigate('/auth/login'));
			return;
		}
	};

	return (
		<S.Container>
			<S.ContentBox>
				<S.ContentTitle>{t('society.register.title')}</S.ContentTitle>
				<S.BasicInfoButtonBox>
					<PrimaryButton type="button" onClick={handleRegister} disabled={isRegistering}>
						{t('society.register.actions.pay')}
						<SingleArrowNext iconColor="currentColor" width="20" height="20" />
					</PrimaryButton>
					<OutlineButton type="button" onClick={handleConfirm}>
						{t('society.register.actions.confirm')}
						<SingleArrowNext iconColor="currentColor" width="20" height="20" />
					</OutlineButton>
				</S.BasicInfoButtonBox>
				<S.ContentDescription>
					<li>{t('society.register.notice.period', { end: formatLongDate(END_DATE) })}</li>
					<li>{t('society.register.notice.paymentMethod')}</li>
					<li>
						{t('society.register.notice.refund', { date: formatShortDate(REFUND_DATE) })}
						<br />
						{t('society.register.notice.noRefund', { date: formatShortDate(REFUND_DATE) })}
					</li>
				</S.ContentDescription>
			</S.ContentBox>
			<S.ContentBox>
				<S.ContentBox>
					<S.ContentTitle>{t('society.register.feeTitle')}</S.ContentTitle>
					<S.TablePanel>
						<S.Table>
							<S.TableHeadCell className="item2 center">{t('society.register.feeTable.category')}</S.TableHeadCell>
							<S.TableHeadCell className="center">{t('society.register.feeTable.conferenceFee')}</S.TableHeadCell>

							<S.TableCell className="item3 point center">{t('society.register.feeTable.memberType')}</S.TableCell>
							<S.TableCell className="min-height point center">{t('society.register.feeTable.professor')}</S.TableCell>
							<S.TableCell className="min-height center">{t('society.register.feeTable.professorFee')}</S.TableCell>

							<S.TableCell className="min-height point center">{t('society.register.feeTable.graduate')}</S.TableCell>
							<S.TableCell className="min-height center">{t('society.register.feeTable.graduateFee')}</S.TableCell>

							<S.TableCell className="min-height point center">{t('society.register.feeTable.student')}</S.TableCell>
							<S.TableCell className="min-height center">{t('society.register.feeTable.studentFee')}</S.TableCell>
						</S.Table>
					</S.TablePanel>
				</S.ContentBox>
			</S.ContentBox>
			{isOpen && (
				<ModalPortal>
					<ModalBox onClick={() => setIsOpen(false)}>
						<PaymentModal closeModal={setIsOpen} goodsName={PAYMENT_GOODS_CONFERENCE_FEE} memberType={memberType} />
					</ModalBox>
				</ModalPortal>
			)}
		</S.Container>
	);
};

export default SocietyRegister;
